import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Container,
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import useScrollTo from '../../hooks/useScrollTo';
import { NAV_ITEMS, PEGASUS_URLS } from '../../utils/constants';
import logo from '../../assets/logo-no-background.png';

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollToSection } = useScrollTo();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (sectionId) => {
    scrollToSection(sectionId);
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const handleExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'primary.main',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="ITorama Logo"
          sx={{
            height: 40,
            width: 'auto',
            filter: 'brightness(0) invert(1)',
          }}
        />
        <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      <List sx={{ flexGrow: 1, pt: 2 }}>
        {NAV_ITEMS.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              onClick={() => handleNavClick(item.id)}
              sx={{
                py: 1.5,
                px: 3,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '1rem',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          onClick={() => handleExternalLink(PEGASUS_URLS.login)}
          sx={{ py: 1.2 }}
        >
          Login
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => handleExternalLink(PEGASUS_URLS.signup)}
          sx={{
            py: 1.2,
            borderColor: 'rgba(255, 255, 255, 0.5)',
            color: 'white',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Start Free
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        component={motion.header}
        position="fixed"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        sx={{
          backgroundColor: scrolled ? 'rgba(248, 249, 252, 0.95)' : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          boxShadow: scrolled ? '0px 4px 20px rgba(30, 58, 95, 0.08)' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 70, md: 80 },
              justifyContent: 'space-between',
            }}
          >
            <Box
              component={motion.div}
              whileHover={{ scale: 1.02 }}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => handleNavClick('home')}
            >
              <Box
                component="img"
                src={logo}
                alt="ITorama Property Management"
                sx={{
                  height: { xs: 40, md: 50 },
                  width: 'auto',
                }}
              />
            </Box>

            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { md: 2, lg: 3 },
                }}
              >
                {NAV_ITEMS.map((item) => (
                  <Box
                    key={item.id}
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleNavClick(item.id)}
                    sx={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: theme.typography.fontFamily,
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      color: 'text.primary',
                      padding: '8px 12px',
                      borderRadius: 1,
                      position: 'relative',
                      transition: 'color 0.2s ease',
                      '&:hover': {
                        color: 'secondary.main',
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 4,
                        left: '50%',
                        width: 0,
                        height: 2,
                        backgroundColor: 'secondary.main',
                        transition: 'all 0.2s ease',
                        transform: 'translateX(-50%)',
                      },
                      '&:hover::after': {
                        width: '60%',
                      },
                    }}
                  >
                    {item.label}
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {!isMobile && (
                <>
                  <Button
                    component={motion.button}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    variant="contained"
                    color="primary"
                    onClick={() => handleExternalLink(PEGASUS_URLS.login)}
                    sx={{ px: 3 }}
                  >
                    Login
                  </Button>
                  <Button
                    component={motion.button}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    variant="outlined"
                    color="primary"
                    onClick={() => handleExternalLink(PEGASUS_URLS.signup)}
                    sx={{ px: 3 }}
                  >
                    Start Free
                  </Button>
                </>
              )}

              {isMobile && (
                <IconButton
                  color="primary"
                  aria-label="open navigation menu"
                  onClick={handleDrawerToggle}
                  sx={{
                    backgroundColor: 'rgba(30, 58, 95, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(30, 58, 95, 0.1)',
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <AnimatePresence>
        {isMobile && (
          <Drawer
            anchor="right"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            PaperProps={{
              component: motion.div,
              initial: { x: '100%' },
              animate: { x: 0 },
              exit: { x: '100%' },
              transition: { duration: 0.3, ease: 'easeOut' },
            }}
          >
            {drawer}
          </Drawer>
        )}
      </AnimatePresence>

      <Toolbar sx={{ minHeight: { xs: 70, md: 80 } }} />
    </>
  );
};

export default Navbar;
